import type { HttpResponse } from "@similarweb/pro-api-client";
import { createApiClient, HttpClient } from "@similarweb/pro-api-client";

import {
    customFetch,
    FetchResponseError,
    getErrorMessage,
    getResponseError,
    withFullResponseSymbol,
} from "app/services/fetchService";
import { getHttpHeaders } from "app/services/fetchService/httpHeaders";

const httpClient = new HttpClient({
    baseApiParams: {
        credentials: "same-origin",
        headers: {
            ...getHttpHeaders(),
        },
    },
    customFetch,
});

const originalRequest = httpClient.request;
export const customRequest = async <T = unknown, E = unknown>(
    ...params: Parameters<typeof originalRequest>
): Promise<T> => {
    const [requestParams] = params;
    // Refresh shared headers on every call so per-request values (e.g. page view id)
    // stay fresh instead of reusing the snapshot taken at module init.
    // Explicit per-call headers still win.
    if (requestParams) {
        requestParams.headers = {
            ...getHttpHeaders(),
            ...(requestParams.headers as Record<string, string> | undefined),
        };
    }

    let response: HttpResponse<T, E> | null = null;
    try {
        response = await originalRequest<T, E>(...params);

        if (response.error) {
            throw new FetchResponseError(getErrorMessage(response.error), response, withFullResponseSymbol);
        }

        return response.data;
    } catch (e) {
        // If the server does not return no content header (204) on delete requests, we need to handle it here
        // Otherwise the api client tries to parse the response as json and fails
        if (response?.ok && response?.status === 204) {
            return response.data;
        }

        const { body, method, path } = params[0];

        if (e instanceof FetchResponseError) {
            throw await getResponseError({
                body: body as object | undefined,
                error: e,
                method,
                url: path,
            });
        }

        throw e;
    }
};

httpClient.request = customRequest;

type OriginalApiClient = ReturnType<typeof createApiClient>;

export type Tag = keyof OriginalApiClient;
export type MethodName<T extends Tag> = Exclude<keyof OriginalApiClient[T], "http">;

type TransformedRequest<T extends Tag, M extends MethodName<T>> = OriginalApiClient[T][M] extends (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...args: any[]
) => Promise<HttpResponse<infer D>>
    ? (...args: Parameters<OriginalApiClient[T][M]>) => Promise<D>
    : never;

export type ApiClient = {
    [T in Tag]: {
        [M in MethodName<T>]: TransformedRequest<T, M>;
    };
};

export const apiClient = createApiClient(httpClient) as unknown as ApiClient;
